export class StrictJsonError extends SyntaxError {
    offset;
    constructor(message, offset) {
        super(`${message} at byte ${offset}`);
        this.offset = offset;
        this.name = "StrictJsonError";
    }
}
function assertUnicodeScalarString(value, offset) {
    for (let index = 0; index < value.length; index += 1) {
        const unit = value.charCodeAt(index);
        if (unit >= 0xd800 && unit <= 0xdbff) {
            const next = value.charCodeAt(index + 1);
            if (!(next >= 0xdc00 && next <= 0xdfff)) {
                throw new StrictJsonError("Unpaired high surrogate", offset);
            }
            index += 1;
        }
        else if (unit >= 0xdc00 && unit <= 0xdfff) {
            throw new StrictJsonError("Unpaired low surrogate", offset);
        }
    }
}
class Parser {
    input;
    offset = 0;
    constructor(input) {
        this.input = input;
    }
    parse() {
        this.skipWhitespace();
        const value = this.parseValue();
        this.skipWhitespace();
        if (this.offset !== this.input.length) {
            this.fail("Trailing input");
        }
        return value;
    }
    parseValue() {
        const current = this.input[this.offset];
        switch (current) {
            case "{":
                return this.parseObject();
            case "[":
                return this.parseArray();
            case "\"":
                return this.parseString();
            case "t":
                return this.parseLiteral("true", true);
            case "f":
                return this.parseLiteral("false", false);
            case "n":
                return this.parseLiteral("null", null);
            default:
                if (current === "-" || (current !== undefined && current >= "0" && current <= "9")) {
                    return this.parseNumber();
                }
                this.fail("Expected a JSON value");
        }
    }
    parseObject() {
        this.offset += 1;
        this.skipWhitespace();
        const result = Object.create(null);
        const keys = new Set();
        if (this.consume("}"))
            return result;
        while (true) {
            if (this.input[this.offset] !== "\"")
                this.fail("Expected an object key");
            const keyOffset = this.offset;
            const key = this.parseString();
            if (keys.has(key))
                throw new StrictJsonError(`Duplicate object key ${JSON.stringify(key)}`, keyOffset);
            keys.add(key);
            this.skipWhitespace();
            if (!this.consume(":"))
                this.fail("Expected ':' after object key");
            this.skipWhitespace();
            result[key] = this.parseValue();
            this.skipWhitespace();
            if (this.consume("}"))
                return result;
            if (!this.consume(","))
                this.fail("Expected ',' or '}'");
            this.skipWhitespace();
        }
    }
    parseArray() {
        this.offset += 1;
        this.skipWhitespace();
        const result = [];
        if (this.consume("]"))
            return result;
        while (true) {
            result.push(this.parseValue());
            this.skipWhitespace();
            if (this.consume("]"))
                return result;
            if (!this.consume(","))
                this.fail("Expected ',' or ']'");
            this.skipWhitespace();
        }
    }
    parseString() {
        const start = this.offset;
        this.offset += 1;
        while (this.offset < this.input.length) {
            const code = this.input.charCodeAt(this.offset);
            if (code === 0x22) {
                this.offset += 1;
                const token = this.input.slice(start, this.offset);
                const value = JSON.parse(token);
                assertUnicodeScalarString(value, start);
                return value;
            }
            if (code < 0x20)
                this.fail("Unescaped control character in string");
            if (code === 0x5c) {
                this.offset += 1;
                const escaped = this.input[this.offset];
                if (escaped === "u") {
                    const hex = this.input.slice(this.offset + 1, this.offset + 5);
                    if (!/^[0-9a-fA-F]{4}$/.test(hex))
                        this.fail("Invalid Unicode escape");
                    this.offset += 5;
                    continue;
                }
                if (escaped === undefined || !'\"\\/bfnrt'.includes(escaped))
                    this.fail("Invalid string escape");
            }
            this.offset += 1;
        }
        this.fail("Unterminated string");
    }
    parseNumber() {
        const rest = this.input.slice(this.offset);
        const match = /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?/.exec(rest);
        if (match === null)
            this.fail("Invalid number");
        const token = match[0];
        const boundary = rest[token.length];
        if (boundary !== undefined && !/[\s,\]}]/.test(boundary))
            this.fail("Invalid number boundary");
        this.offset += token.length;
        const value = Number(token);
        if (!Number.isFinite(value))
            throw new StrictJsonError("Non-finite number", this.offset - token.length);
        if (Number.isInteger(value) && !Number.isSafeInteger(value)) {
            throw new StrictJsonError("Integer is outside the interoperable range", this.offset - token.length);
        }
        return value;
    }
    parseLiteral(token, value) {
        if (this.input.slice(this.offset, this.offset + token.length) !== token)
            this.fail(`Expected ${token}`);
        this.offset += token.length;
        return value;
    }
    skipWhitespace() {
        while (this.offset < this.input.length) {
            const code = this.input.charCodeAt(this.offset);
            if (code === 0x20 || code === 0x09 || code === 0x0a || code === 0x0d)
                this.offset += 1;
            else
                break;
        }
    }
    consume(character) {
        if (this.input[this.offset] !== character)
            return false;
        this.offset += 1;
        return true;
    }
    fail(message) {
        throw new StrictJsonError(message, new TextEncoder().encode(this.input.slice(0, this.offset)).byteLength);
    }
}
export function parseIJson(input) {
    const raw = typeof input === "string" ? input : new TextDecoder("utf-8", { fatal: true }).decode(input);
    return new Parser(raw).parse();
}
export function parseClosedJson(input, schema) {
    return schema.parse(parseIJson(input));
}
export function assertIJson(value) {
    if (value === null || typeof value === "boolean")
        return;
    if (typeof value === "string") {
        assertUnicodeScalarString(value, 0);
        return;
    }
    if (typeof value === "number") {
        if (!Number.isFinite(value) || (Number.isInteger(value) && !Number.isSafeInteger(value))) {
            throw new StrictJsonError("Non-interoperable number", 0);
        }
        return;
    }
    if (Array.isArray(value)) {
        for (const item of value)
            assertIJson(item);
        return;
    }
    if (typeof value === "object") {
        for (const [key, item] of Object.entries(value)) {
            assertUnicodeScalarString(key, 0);
            assertIJson(item);
        }
        return;
    }
    throw new StrictJsonError(`Unsupported JSON value ${typeof value}`, 0);
}
//# sourceMappingURL=strict-json.js.map